import React from 'react'
import ListTime from './ListTime';
import { Business } from '@/config';
import { TFunction } from 'i18next';
import Lucide from '@/components/Base/Lucide';
import { TOrder } from '@/services/chefService';
import { getCurrencyConfig } from '@/utils/auth';
import { formatPriceByCurrency } from '@/utils/helper';
import defaultAvatar from "@/assets/images/default.jpg";
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import dayjs from 'dayjs';
import { translateLanguage } from '@/helper/i18n';

export interface ListProps {
    orders: TOrder[];
    t: TFunction;
    isLoading: boolean;
    renderBTN: (order: TOrder) => React.ReactNode;
    status: string;
    shop: Business;
}

export default function ListItem({ orders, t, isLoading, renderBTN, status, shop }: ListProps) {
    const getCurrency = getCurrencyConfig();

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg p-2 col-span-12 flex flex-col items-center justify-center mt-[20vh] 2xl:mt-[35vh]">
                <AiOutlineLoading3Quarters
                    size={32}
                    className="animate-spin inline-block text-[#767676]"
                />
            </div>
        );
    }

    return (
        <>
            {orders.length === 0 ?
                (
                    <div className="rounded-lg mt-4">
                        <div className="bg-white rounded-lg p-2 col-span-12 flex flex-col items-center justify-center mt-[15vh] 2xl:mt-[30vh]">
                            <img className="w-32 h-32" src={defaultAvatar} alt="" />
                            <div className="text-lg font-semibold mt-5">
                                {t("noOrders")}
                            </div>
                        </div>
                    </div>
                )
                :
                (
                    orders.map((order, index) => (
                        <div
                            key={index}
                            className="grid grid-cols-12 mb-2 p-3 bg-gray-100 rounded-md"
                        >
                            <div className="col-span-1 flex items-center font-bold">
                                {order.code || "N/A"}
                            </div>

                            <div className="col-span-2 flex flex-col justify-center font-bold">
                                <span>{translateLanguage(order.name, order.nameKH || order.name) || "N/A"}</span>
                                {order.options && order.options.length > 0 && (
                                    <div className="text-xs font-normal text-gray-500 mt-1 flex flex-col gap-0.5">
                                        {order.options.map((opt, optIdx) => {
                                            const optName = translateLanguage(opt.name, opt.nameKH || opt.name);
                                            const valNames = (opt.values || [])
                                                .map((val) => translateLanguage(val.name, val.nameKH || val.name))
                                                .filter(Boolean)
                                                .join(', ');
                                            if (!valNames) return null;
                                            return (
                                                <span key={opt._id || optIdx}>
                                                    {optName}: {valNames}
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="col-span-2 flex items-center font-bold">
                                {dayjs(order?.statusTracking?.pending ).format("HH:mm:ss") || "N/A"}
                            </div>

                            <div className="col-span-3 pl-5 grid grid-cols-4 items-center">
                                <div className="col-span-1 text-red-600 font-medium">
                                    {formatPriceByCurrency(order.price, getCurrency?.symbol)}
                                </div>
                                <div className="col-span-1 pl-2">
                                    X {order.quantity || "N/A"}
                                </div>
                                <div className="col-span-1 text-blue-700 flex">
                                    <Lucide
                                        icon="Martini"
                                        className="w-4 h-4 text-gray-900 mr-1"
                                    />
                                    {order.tableName || "N/A"}
                                </div>
                            </div>

                            <div className="col-span-4 flex justify-between pl-5">
                                <div className="flex gap-2 items-center">
                                    <Lucide
                                        icon="MessageSquareText"
                                        className="w-5 h-5 mr-1"
                                    />
                                    {order.instruction || "N/A"}
                                </div>

                                <div className="flex items-center gap-2">
                                    {status === "Ready" && (
                                        <ListTime order={order} shop={shop} />
                                    )}
                                    {renderBTN(order)}
                                </div>
                            </div>
                        </div>
                    ))
                )
            }
        </>
    );
}
